/**
 * Iter 84.4 / Phase E5 Sprint 4 — Gratuity Audit Console (D40).
 *
 * Operator generates a point-in-time gratuity audit pack:
 *   • Preview eligibility + estimated provision per employee
 *   • Generate an immutable batch (D40-D snapshot) with full audit metadata
 *   • Download register.csv, provision.csv (by dept / BU), full audit-pack.zip
 *
 * Uses fnf_engine math via the backend — no client-side gratuity calc.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Sparkle, DownloadSimple, ArrowsClockwise, Eye, FileZip, Package,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";

const fmtMoney = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);

export default function GratuityAuditConsole() {
    const { can } = useAuth();
    const canAdmin = can("compliance:admin");

    const [asOfDate, setAsOfDate] = useState(today());
    const [department, setDepartment] = useState("");
    const [businessUnit, setBusinessUnit] = useState("");
    const [onlyEligible, setOnlyEligible] = useState(false);

    const [batches, setBatches] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/compliance/gratuity-audit/batches");
            setBatches(data.items || []);
        } catch (err) { /* non-fatal */ }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const buildBody = () => ({
        as_of_date: asOfDate,
        department: department || null,
        business_unit: businessUnit || null,
        only_eligible: onlyEligible,
    });

    const handlePreview = async () => {
        if (!asOfDate) { toast.error("Pick an As-of date"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/gratuity-audit/preview", buildBody());
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!asOfDate) { toast.error("Pick an As-of date"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/gratuity-audit/generate", {
                ...buildBody(),
                note: `As-of ${asOfDate} via Gratuity Audit console`,
            });
            toast.success(
                `Gratuity audit batch generated · ${data.employee_count} employees · ${fmtMoney(data.total_estimated_provision)} estimated`
            );
            setPreviewOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Generate failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (batchId, kind) => {
        try {
            let url = "";
            let filename = "";
            if (kind === "register") {
                url = `/compliance/gratuity-audit/batches/${batchId}/register.csv`;
                filename = `GratuityRegister_${batchId}.csv`;
            } else if (kind === "provision-dept") {
                url = `/compliance/gratuity-audit/batches/${batchId}/provision.csv?by=department`;
                filename = `GratuityProvision_dept_${batchId}.csv`;
            } else if (kind === "provision-bu") {
                url = `/compliance/gratuity-audit/batches/${batchId}/provision.csv?by=business_unit`;
                filename = `GratuityProvision_bu_${batchId}.csv`;
            } else if (kind === "zip") {
                url = `/compliance/gratuity-audit/batches/${batchId}/audit-pack.zip`;
                filename = `GratuityAuditPack_${batchId}.zip`;
            }
            const r = await api.get(url, { responseType: "blob" });
            const mime = kind === "zip" ? "application/zip" : "text/csv";
            const blob = new Blob([r.data], { type: mime });
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl; a.download = filename; a.click();
            URL.revokeObjectURL(objUrl);
            toast.success(`Downloaded ${filename}`);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="gratuity-audit-console">
            <PageHeader
                title="Gratuity Audit Pack"
                subtitle="Statutory point-in-time provision snapshot · D40-D immutable · D40-F full audit metadata"
                actions={
                    <Button size="sm" variant="outline" onClick={reload} data-testid="gratuity-audit-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-4 bg-slate-50/30 space-y-3"
                    data-testid="gratuity-audit-builder">
                    <h3 className="font-semibold text-sm">Generate Audit Pack</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">As-of Date</Label>
                            <Input type="date" value={asOfDate}
                                onChange={(e) => setAsOfDate(e.target.value)}
                                className="font-mono" data-testid="gratuity-audit-as-of-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Department (optional)</Label>
                            <Input value={department} onChange={(e) => setDepartment(e.target.value)}
                                placeholder="e.g. Engineering" data-testid="gratuity-audit-dept-input" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Business Unit (optional)</Label>
                            <Input value={businessUnit} onChange={(e) => setBusinessUnit(e.target.value)}
                                placeholder="e.g. BU-1" data-testid="gratuity-audit-bu-input" />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={onlyEligible}
                            onChange={(e) => setOnlyEligible(e.target.checked)}
                            data-testid="gratuity-audit-only-eligible" />
                        Only include eligible employees (≥ 5 years continuous service)
                    </label>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handlePreview}
                            disabled={loading} data-testid="gratuity-audit-preview-btn">
                            <Eye size={14} className="mr-1" /> Preview
                        </Button>
                        {canAdmin && (
                            <Button size="sm" onClick={handleGenerate}
                                disabled={loading} data-testid="gratuity-audit-generate-btn">
                                <Sparkle size={14} className="mr-1" /> Generate Batch
                            </Button>
                        )}
                    </div>
                </div>

                <div className="border rounded p-4 bg-slate-50/30 text-xs space-y-2">
                    <h3 className="font-semibold text-sm">D40 Locks</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>D40-A:</strong> Output is a 5-file ZIP (register · provision-by-dept · provision-by-bu · per-employee formula · summary).</li>
                        <li><strong>D40-B:</strong> Statutory formula <code>15 × (Basic + DA) × Years ÷ 26</code>; 6-month rounding rule; 5-year eligibility gate.</li>
                        <li><strong>D40-C:</strong> Last-drawn wages sourced from the latest <em>APPROVED</em> payroll run; falls back to <code>payroll_assignments</code>.</li>
                        <li><strong>D40-D:</strong> Each batch is immutable; new generation = new batch (old preserved).</li>
                        <li><strong>D40-F:</strong> Audit metadata stamped on every batch — <code>generated_by</code>, <code>generated_at</code>, <code>employee_count</code>, <code>calculation_basis</code>, <code>snapshot_version</code>.</li>
                        <li className="text-amber-700"><strong>Disclaimer:</strong> Estimated provision is NOT a payout commitment.</li>
                    </ul>
                </div>
            </div>

            {/* Generated batches */}
            <div className="border rounded" data-testid="gratuity-audit-batches-table">
                <div className="p-2 bg-slate-50 border-b text-sm font-semibold">
                    Generated Batches · {batches.length}
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px]">
                        <tr>
                            <th className="text-left px-2 py-1">Batch</th>
                            <th className="text-left px-2 py-1">As-of</th>
                            <th className="text-right px-2 py-1">Employees</th>
                            <th className="text-right px-2 py-1">Eligible</th>
                            <th className="text-right px-2 py-1">Estimated</th>
                            <th className="text-right px-2 py-1">Accrued</th>
                            <th className="text-right px-2 py-1">Gap</th>
                            <th className="text-left px-2 py-1">Generated</th>
                            <th className="text-left px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {batches.length === 0 && (
                            <tr><td colSpan="9" className="text-center text-slate-400 p-6">
                                No gratuity audit batches yet
                            </td></tr>
                        )}
                        {batches.map((b) => (
                            <tr key={b.id} className="border-b" data-testid={`gratuity-audit-row-${b.id}`}>
                                <td className="px-2 py-1 font-mono">{b.id}</td>
                                <td className="px-2 py-1 font-mono">{b.as_of_date}</td>
                                <td className="px-2 py-1 text-right font-mono">{b.employee_count}</td>
                                <td className="px-2 py-1 text-right font-mono">{b.n_eligible}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(b.total_estimated_provision)}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(b.total_accrued_to_date)}</td>
                                <td className={`px-2 py-1 text-right font-mono ${b.total_gap > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                    {fmtMoney(b.total_gap)}
                                </td>
                                <td className="px-2 py-1 text-[10px] text-slate-500">
                                    {b.generated_by_email}<br />
                                    {b.generated_at?.slice(0, 16).replace("T", " ")}
                                </td>
                                <td className="px-2 py-1 flex gap-1 flex-wrap">
                                    <Button size="sm" variant="outline" onClick={() => handleDownload(b.id, "register")}
                                        data-testid={`gratuity-download-register-${b.id}`} title="Register CSV">
                                        <DownloadSimple size={12} />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => handleDownload(b.id, "provision-dept")}
                                        data-testid={`gratuity-download-provision-dept-${b.id}`} title="Provision by Dept CSV">
                                        <Package size={12} />
                                    </Button>
                                    {canAdmin && (
                                        <Button size="sm" onClick={() => handleDownload(b.id, "zip")}
                                            data-testid={`gratuity-download-zip-${b.id}`} title="Audit Pack ZIP">
                                            <FileZip size={12} className="mr-1" /> ZIP
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Gratuity Audit Preview · {preview?.as_of_date}</DialogTitle>
                        <DialogDescription className="text-xs">
                            {preview?.calculation_basis}
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="text-xs space-y-3">
                            <div className="grid grid-cols-3 gap-2">
                                <SummaryCard label="Total Employees" value={preview.n_employees} />
                                <SummaryCard label="Eligible (≥5 yrs)" value={preview.n_eligible} tone="emerald" />
                                <SummaryCard label="Ineligible" value={preview.n_ineligible} tone="slate" />
                                <SummaryCard label="Total Estimated Provision" value={fmtMoney(preview.total_estimated_provision)} tone="blue" />
                                <SummaryCard label="Total Accrued To Date" value={fmtMoney(preview.total_accrued_to_date)} />
                                <SummaryCard label="Provisioning Gap" value={fmtMoney(preview.total_gap)}
                                    tone={preview.total_gap > 0 ? "rose" : "emerald"} />
                            </div>
                            <div className="max-h-72 overflow-y-auto border rounded">
                                <table className="w-full text-[10px]" data-testid="gratuity-preview-table">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr>
                                            <th className="text-left px-1 py-0.5">Code</th>
                                            <th className="text-left px-1 py-0.5">Name</th>
                                            <th className="text-left px-1 py-0.5">Dept</th>
                                            <th className="text-left px-1 py-0.5">DOJ</th>
                                            <th className="text-right px-1 py-0.5">Years</th>
                                            <th className="text-left px-1 py-0.5">Eligible</th>
                                            <th className="text-right px-1 py-0.5">Last Drawn</th>
                                            <th className="text-right px-1 py-0.5">Estimated</th>
                                            <th className="text-right px-1 py-0.5">Gap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.records || []).slice(0, 200).map((r) => (
                                            <tr key={r.employee_id} className="border-b">
                                                <td className="px-1 py-0.5 font-mono">{r.employee_code}</td>
                                                <td className="px-1 py-0.5">{r.employee_name}</td>
                                                <td className="px-1 py-0.5">{r.department}</td>
                                                <td className="px-1 py-0.5 font-mono">{r.date_of_joining}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{r.years_of_service}</td>
                                                <td className="px-1 py-0.5">
                                                    {r.eligible ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge>
                                                    ) : (
                                                        <Badge className="bg-slate-100 text-slate-600">No</Badge>
                                                    )}
                                                </td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(r.last_drawn_basic_da)}</td>
                                                <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(r.estimated_gratuity)}</td>
                                                <td className={`px-1 py-0.5 text-right font-mono ${r.accrual_vs_estimate_gap > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                                    {fmtMoney(r.accrual_vs_estimate_gap)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                                snapshot_version={preview.snapshot_version}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canAdmin && (
                            <Button onClick={handleGenerate} disabled={loading}
                                data-testid="gratuity-audit-generate-from-preview-btn">
                                <Sparkle size={14} className="mr-1" /> Generate Batch
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SummaryCard({ label, value, tone }) {
    const toneMap = {
        emerald: "bg-emerald-50 border-emerald-200",
        rose: "bg-rose-50 border-rose-200",
        amber: "bg-amber-50 border-amber-200",
        blue: "bg-blue-50 border-blue-200",
        slate: "bg-slate-50 border-slate-200",
    };
    return (
        <div className={`border rounded p-2 ${toneMap[tone] || "bg-slate-50/30"}`}>
            <div className="text-[9px] uppercase text-slate-500 tracking-wider">{label}</div>
            <div className="font-mono text-sm font-bold mt-0.5">{value}</div>
        </div>
    );
}
